import { subscribe } from 'valtio';

import type { AsyncState } from '../types';

/**
 * Returns a promise that resolves when an AsyncState becomes ready.
 * If already ready, resolves immediately. If errored, rejects immediately.
 *
 * @param proxy - The valtio proxy object containing the AsyncState
 * @param selector - Function to select the AsyncState from the proxy
 *
 * @example
 * const apiKeyData = await waitForAsyncState(
 *   userStore.state,
 *   (s) => s.apiKey
 * );
 */
export function waitForAsyncState<P extends object, T>(
  proxy: P,
  selector: (state: P) => AsyncState<T>,
): Promise<T | undefined> {
  const state = selector(proxy);

  if (state.status === 'ready') {
    return Promise.resolve(state.data);
  }

  if (state.status === 'error') {
    return Promise.reject(new Error(state.error));
  }

  return new Promise((resolve, reject) => {
    const unsubscribe = subscribe(proxy, () => {
      const current = selector(proxy);
      if (current.status === 'ready') {
        unsubscribe();
        resolve(current.data);
      } else if (current.status === 'error') {
        unsubscribe();
        reject(new Error(current.error));
      }
    });
  });
}
